/**
 * useMessageTemplates — camada de dados dos Modelos de Mensagens.
 *
 * ASSIMETRIA DELIBERADA (leia antes de "consertar"):
 *  - LEITURA é offline-first (cache local + warmup). Compor a mensagem é local,
 *    então o usuário tem que conseguir enviar pelo WhatsApp sem rede.
 *  - GERENCIAMENTO (criar/editar/excluir) é ONLINE-ONLY, de propósito. Modelos
 *    são CONFIGURAÇÃO, não dado de negócio: enfileirá-los criaria uma superfície
 *    inteira de sync (id temporário, tradução de id, dobra de operações) para
 *    algo que o usuário mexe uma vez por mês. Precedente no app: edição do
 *    catálogo (CatalogPage.tsx). NÃO existe `create/update/delete_message_template`
 *    no executor offline — e não deve existir.
 *
 * Escopo por EMPRESA (`company_id`): a equipe compartilha os modelos. Todo
 * insert carrega `company_id` — sem ele a RLS de team accounts rejeita.
 */
import { useCallback, useMemo, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { supabase } from '@/integrations/supabase/client';
import { checkNetworkStatus } from '@/hooks/useNetworkStatus';
import { cacheMessageTemplates, getCachedMessageTemplates } from '@/lib/offlineDataCache';
import {
  DEFAULT_TEMPLATES,
  pickDefaultTemplate,
  type MessageTemplateCategory,
} from '@/lib/messageTemplates';
import { toast } from 'sonner';

const TEMPLATES_KEY = 'message_templates';

export interface MessageTemplate {
  id: string;
  company_id: string;
  user_id: string;
  name: string;
  category: MessageTemplateCategory;
  body: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface MessageTemplateInput {
  name: string;
  category: MessageTemplateCategory;
  body: string;
  is_default?: boolean;
}

/** Erro sentinela: toda mutação online-only rejeita com isto quando sem rede. */
const OFFLINE = 'offline';

function isOfflineError(error: unknown): boolean {
  return error instanceof Error && error.message === OFFLINE;
}

/**
 * Toast do erro de uma mutação. Sem rede é `warning` (regra de bloqueio que o
 * usuário resolve reconectando), não `error` — ver a taxonomia em CLAUDE.md #26.
 */
function toastMutationError(error: unknown, action: string, fallback: string): void {
  if (isOfflineError(error)) toast.warning(`${action} exige conexão com a internet.`);
  else toast.error(fallback);
}

// ===== Leitura =====

/**
 * Todos os modelos da empresa ativa. Offline-first: serve do cache local na
 * hora e revalida quando há rede.
 */
export function useMessageTemplates() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [TEMPLATES_KEY, companyId],
    enabled: Boolean(user?.id && companyId),
    networkMode: 'offlineFirst',
    staleTime: 60 * 1000,
    // Outro membro da equipe pode ter criado/editado um modelo; como não há
    // evento local para isso, revalida a cada montagem. Offline-first continua
    // servindo o cache instantaneamente (e quando não há rede). Espelha useContracts.
    refetchOnMount: 'always',
    // A query nasce DESABILITADA (sessão/empresa resolvem depois da montagem), então
    // `refetchOnMount` já passou quando ela habilita: sem o carimbo, o cache semeado
    // conta como FRESCO e a primeira busca nunca sai (#158).
    initialDataUpdatedAt: 0,
    initialData: () => (user?.id ? getCachedMessageTemplates(user.id) ?? undefined : undefined),
    queryFn: async (): Promise<MessageTemplate[]> => {
      if (!user?.id || !companyId) return [];
      const { data, error } = await supabase
        .from('message_templates')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      const rows = (data ?? []) as unknown as MessageTemplate[];
      cacheMessageTemplates(user.id, rows);
      return rows;
    },
  });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY, companyId] });
  }, [queryClient, companyId]);

  return { ...query, templates: query.data ?? [], invalidate };
}

/**
 * Modelos de UMA categoria + o modelo pré-selecionado. É o que os pontos de
 * envio consomem.
 */
export function useMessageTemplatesByCategory(category: MessageTemplateCategory) {
  const raw = useMessageTemplates();
  const templates = useMemo(
    () => raw.templates.filter((t) => t.category === category),
    [raw.templates, category],
  );
  const defaultTemplate = useMemo(
    () => pickDefaultTemplate(raw.templates, category),
    [raw.templates, category],
  );
  return { ...raw, templates, defaultTemplate };
}

// ===== Semeadura =====

/**
 * Semeia os modelos padrão na PRIMEIRA abertura da tela de gerenciamento, por
 * empresa (mesmo padrão de `useFinanceCategories`). Só roda online, só com
 * permissão de escrita, e nunca mais de uma vez por montagem — daí o ref, que
 * evita duas semeaduras se a query reexecutar enquanto o insert está em voo.
 *
 * Não é pré-requisito para enviar: os pontos de envio já caem em
 * `defaultBodyFor(...)` quando não há nenhum modelo (ver messageTemplates.ts).
 */
export function useSeedDefaultTemplates() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();
  const seedingRef = useRef(false);

  return useCallback(
    async (existingCount: number): Promise<boolean> => {
      if (existingCount > 0) return false;
      if (!user?.id || !companyId) return false;
      if (seedingRef.current) return false;
      if (!(await checkNetworkStatus())) return false;

      seedingRef.current = true;
      try {
        // Recheca no servidor: o cache local pode estar vazio simplesmente por
        // ainda não ter sincronizado, e semear em cima disso duplicaria tudo.
        const { count, error: countError } = await supabase
          .from('message_templates')
          .select('id', { count: 'exact', head: true })
          .eq('company_id', companyId);
        if (countError || (count ?? 0) > 0) return false;

        const rows = DEFAULT_TEMPLATES.map((t) => ({
          name: t.name,
          category: t.category,
          body: t.body,
          is_default: t.is_default,
          user_id: user.id,
          company_id: companyId,
        }));

        const { error } = await supabase.from('message_templates').insert(rows);
        // Falha silenciosa de propósito: outro device pode ter semeado ao mesmo
        // tempo, e um usuário `viewer` (sem permissão de escrita) não deve ver
        // erro só por abrir a tela. O envio continua funcionando via texto padrão.
        if (error) return false;

        queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY, companyId] });
        return true;
      } finally {
        seedingRef.current = false;
      }
    },
    [user?.id, companyId, queryClient],
  );
}

// ===== Mutações (ONLINE-ONLY) =====

/**
 * Garante que só exista um `is_default` por categoria: desmarca os irmãos.
 * Melhor-esforço — se falhar, `pickDefaultTemplate` ainda escolhe o primeiro,
 * então o pior caso é "o padrão é outro", nunca um erro na cara do usuário.
 */
async function clearSiblingDefaults(
  companyId: string,
  category: MessageTemplateCategory,
  exceptId?: string,
): Promise<void> {
  let query = supabase
    .from('message_templates')
    .update({ is_default: false })
    .eq('company_id', companyId)
    .eq('category', category)
    .eq('is_default', true);
  if (exceptId) query = query.neq('id', exceptId);
  await query;
}

export function useCreateMessageTemplate() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();

  return useMutation({
    // offlineFirst: sem isto o TanStack PAUSA a mutation quando o app perde a
    // rede com a tela aberta, e o ramo offline nunca roda (CLAUDE.md #41).
    networkMode: 'offlineFirst' as const,
    mutationFn: async (input: MessageTemplateInput) => {
      if (!user?.id) throw new Error('Sem sessão');
      if (!companyId) throw new Error('Nenhuma empresa ativa');
      if (!(await checkNetworkStatus())) throw new Error(OFFLINE);

      if (input.is_default) await clearSiblingDefaults(companyId, input.category);

      const { data, error } = await supabase
        .from('message_templates')
        .insert({
          name: input.name.trim(),
          category: input.category,
          body: input.body,
          is_default: Boolean(input.is_default),
          user_id: user.id,
          company_id: companyId, // obrigatório: RLS de team accounts
        })
        .select('*')
        .single();
      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY, companyId] });
      return data as unknown as MessageTemplate;
    },
    onError: (error) =>
      toastMutationError(error, 'Criar modelos de mensagem', 'Não foi possível salvar o modelo.'),
  });
}

export function useUpdateMessageTemplate() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();

  return useMutation({
    // offlineFirst: sem isto o TanStack PAUSA a mutation quando o app perde a
    // rede com a tela aberta, e o ramo offline nunca roda (CLAUDE.md #41).
    networkMode: 'offlineFirst' as const,
    mutationFn: async (input: { id: string; updates: Partial<MessageTemplateInput> }) => {
      if (!user?.id) throw new Error('Sem sessão');
      if (!companyId) throw new Error('Nenhuma empresa ativa');
      if (!(await checkNetworkStatus())) throw new Error(OFFLINE);

      const fields: Record<string, unknown> = {};
      if (input.updates.name !== undefined) fields.name = input.updates.name.trim();
      if (input.updates.category !== undefined) fields.category = input.updates.category;
      if (input.updates.body !== undefined) fields.body = input.updates.body;
      if (input.updates.is_default !== undefined) fields.is_default = input.updates.is_default;
      if (Object.keys(fields).length === 0) return;

      if (input.updates.is_default && input.updates.category) {
        await clearSiblingDefaults(companyId, input.updates.category, input.id);
      }

      const { error } = await supabase
        .from('message_templates')
        .update(fields)
        .eq('id', input.id)
        .eq('company_id', companyId);
      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY, companyId] });
    },
    onError: (error) =>
      toastMutationError(
        error,
        'Editar modelos de mensagem',
        'Não foi possível atualizar o modelo.',
      ),
  });
}

export function useDeleteMessageTemplate() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();

  return useMutation({
    // offlineFirst: sem isto o TanStack PAUSA a mutation quando o app perde a
    // rede com a tela aberta, e o ramo offline nunca roda (CLAUDE.md #41).
    networkMode: 'offlineFirst' as const,
    mutationFn: async (id: string) => {
      if (!user?.id) throw new Error('Sem sessão');
      if (!companyId) throw new Error('Nenhuma empresa ativa');
      if (!(await checkNetworkStatus())) throw new Error(OFFLINE);

      const { error } = await supabase
        .from('message_templates')
        .delete()
        .eq('id', id)
        .eq('company_id', companyId);
      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY, companyId] });
    },
    onError: (error) =>
      toastMutationError(error, 'Excluir modelos de mensagem', 'Não foi possível excluir o modelo.'),
  });
}
